import React from "react";

/**
 * One cell of the JetBrains Air layout grid: the layout's text structure with
 * the brand treatment the column stands for (see `variants` in
 * data/products/jetbrains-air.js). Drawn inline because a CDN thumbnail shows
 * one axis only, and the grid has to show both at a glance.
 *
 * The geometry is the Releases CDN icon set's (`/Layouts/releases/<id>.svg`):
 * flat #B5B5B5 bars on a 90×50 canvas, the text block from x=5, stacked from
 * the bottom edge with a 2px gap, the screenshot window at the right. The
 * treatment changes the block: Modul headlines are the heavy bars as drawn
 * there (1 and 3); the "air" wordmark over a JetBrains Sans headline puts the
 * dot triangle over lighter bars (2); the Air logo mark puts the same triangle
 * at the top right (3). Modul and the mark are dotted forms, so one dotted
 * glyph stands for both: `DotTriangle` is the only dotted shape the grid
 * draws, and only its position changes between the two roles.
 */

const FILL = "#B5B5B5";
const VB_W = 90;
const VB_H = 50;
const TEXT_X = 5;
const BOTTOM = 45;
const GAP = 2;

/** A block in the text stack: a bar of `h` px or the dotted wordmark. */
type Block = {kind: "bar"; h: number} | {kind: "dots"};

// The dotted glyph, one shape for both dotted roles: three dots in rows of 1
// and 2, apex up. `DOT_R` and the two steps set its whole geometry, so the
// wordmark in the text stack and the mark in the corner cannot drift apart.
const DOT_R = 1.5;
const DOT_DX = 4.5;
const DOT_DY = 4;
const TRI_ROWS = [1, 2];
const TRI_W = DOT_DX * (TRI_ROWS.length - 1) + DOT_R * 2;
const TRI_H = DOT_DY * (TRI_ROWS.length - 1) + DOT_R * 2;

type Body = {
  /** Width of the text block. */
  textWidth: number;
  /** Headline for the Modul treatments (1 and 3), top to bottom. */
  modul: Block[];
  /** Wordmark over a Sans headline (treatment 2), top to bottom. */
  sans: Block[];
  /** Screenshot window at the right. */
  window?: boolean;
};

const bar = (h: number): Block => ({kind: "bar", h});
const dots: Block = {kind: "dots"};

const BODIES: Record<number, Body> = {
  // Headline alone.
  1: {textWidth: 70, modul: [bar(9), bar(9)], sans: [dots, bar(5), bar(5)]},
  // Headline over a subtitle.
  3: {textWidth: 70, modul: [bar(9), bar(2), bar(2)], sans: [dots, bar(5), bar(2), bar(2)]},
  // Headline beside a screenshot.
  5: {textWidth: 30, modul: [bar(5), bar(5)], sans: [dots, bar(4), bar(4)], window: true},
  // The screenshot window on the artwork, no headline.
  20: {textWidth: 30, modul: [], sans: [dots], window: true},
};

/** Layout ids the grid can draw. The policy grants Air these and no others. */
export const AIR_LAYOUT_ICON_IDS: readonly number[] = Object.keys(BODIES).map(Number);

export const hasAirLayoutIcon = (layoutId: number): boolean => layoutId in BODIES;

/** The dot triangle: `x` and `y` are the top left of its TRI_W×TRI_H box. */
const DotTriangle = ({x, y}: {x: number; y: number}) => (
  <g fill={FILL}>
    {TRI_ROWS.map((count, row) =>
      Array.from({length: count}, (_, i) => (
        <circle
          key={`${row}-${i}`}
          cx={x + TRI_W / 2 + (i - (count - 1) / 2) * DOT_DX}
          cy={y + DOT_R + row * DOT_DY}
          r={DOT_R}
        />
      ))
    )}
  </g>
);

/** The wordmark: the triangle flush with the text block. */
const Wordmark = ({y}: {y: number}) => <DotTriangle x={TEXT_X} y={y} />;

/** The Air logo mark: the same triangle, in the top right corner. */
const Mark = () => <DotTriangle x={VB_W - 5 - TRI_W} y={5} />;

/** Blocks stacked upward from the bottom edge, the way the CDN icons sit. */
const Stack = ({blocks, width}: {blocks: Block[]; width: number}) => {
  const out: React.ReactElement[] = [];
  let bottom = BOTTOM;
  for (let i = blocks.length - 1; i >= 0; i--) {
    const block = blocks[i];
    const h = block.kind === "dots" ? TRI_H : block.h;
    const y = bottom - h;
    out.push(
      block.kind === "dots" ? (
        <Wordmark key={i} y={y} />
      ) : (
        <rect key={i} x={TEXT_X} y={y} width={width} height={h} fill={FILL} />
      )
    );
    bottom = y - GAP;
  }
  return <>{out}</>;
};

/** The screenshot window: an outline open to the right edge, a picture glyph inside. */
const Window = () => (
  <g stroke={FILL} strokeWidth={2} fill="none">
    <path d="M38 50V20a5 5 0 0 1 5-5H90" />
    <g strokeWidth={1} strokeLinecap="round" strokeLinejoin="round">
      <rect x={58} y={27} width={12} height={9} rx={1} />
      <path d="M59.5 35l2.5-3 2 2 2-2 3 3" />
    </g>
  </g>
);

const AirLayoutIcon = ({layoutId, variant}: {layoutId: number; variant: number}) => {
  // A layout outside the drawn set falls back to the headline-only body so the
  // cell still shows its treatment; AirLayoutIcon.test.tsx keeps the set in
  // step with the layout policy.
  const body = BODIES[layoutId] ?? BODIES[1];
  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <Stack blocks={variant === 2 ? body.sans : body.modul} width={body.textWidth} />
      {variant === 3 && <Mark />}
      {body.window && <Window />}
    </svg>
  );
};

export default AirLayoutIcon;
